import {SanityDocument} from '@sanity/client'
import {StructureBuilder, StructureResolverContext} from 'sanity/structure'
import {map} from 'rxjs/operators'
import {VscTag} from 'react-icons/vsc'
import {FiAlertCircle} from 'react-icons/fi'

// Type definitions
type TagWithCount = SanityDocument & {postCount: number}

// Helper functions
const pluralize = (count: number, singular: string, plural: string) =>
  count === 1 ? singular : plural

const formatTitle = (title: string | undefined, count: number, singular: string, plural: string) =>
  `${title || 'Untitled'} (${count} ${pluralize(count, singular, plural)})`

export default function tagsStructure(S: StructureBuilder, context: StructureResolverContext) {
  const client = context.getClient({apiVersion: '2023-01-01'})
  const documentStore = {
    listenQuery: (query: string, params?: Record<string, unknown>) =>
      client.observable.fetch(query, params || {}),
  }

  const tagSchemaType = 'blog.tag'
  const postSchemaType = 'blog.post'

  // Queries
  const tagsWithCountQuery = `*[_type == "${tagSchemaType}" && !(_id in path("drafts.**"))]{
    _id,
    title,
    "postCount": count(*[_type == "${postSchemaType}" && !(_id in path("drafts.**")) && references(^._id)])
  } | order(title asc)`

  return S.listItem()
    .title('Tags')
    .icon(VscTag)
    .child(() =>
      documentStore.listenQuery(tagsWithCountQuery, {}).pipe(
        map((tags) => {
          const tagsWithPosts = tags.filter((tag: TagWithCount) => tag.postCount > 0)
          const tagsWithoutPosts = tags.filter((tag: TagWithCount) => tag.postCount === 0)

          return S.list()
            .title('Tags')
            .items([
              // All tags
              S.listItem()
                .title('All Tags')
                .icon(VscTag)
                .child(
                  S.documentList()
                    .title('All Tags')
                    .schemaType(tagSchemaType)
                    .filter(`_type == $type && !(_id in path("drafts.**"))`)
                    .params({type: tagSchemaType}),
                ),

              // Tags without posts
              S.listItem()
                .title(`Tags without posts (${tagsWithoutPosts.length})`)
                .icon(FiAlertCircle)
                .child(
                  S.documentList()
                    .title('Tags without posts')
                    .schemaType(tagSchemaType)
                    .filter(
                      `_type == $type && !(_id in path("drafts.**")) && count(*[_type == "${postSchemaType}" && !(_id in path("drafts.**")) && references(^._id)]) == 0`,
                    )
                    .params({type: tagSchemaType}),
                ),

              S.divider({id: 'divider-by-tag', type: 'divider', title: 'Tags with Posts'}),

              // Individual tags with post counts
              ...tagsWithPosts.map((tag: TagWithCount) =>
                S.listItem()
                  .id(`tag-${tag._id}`)
                  .title(formatTitle(tag.title, tag.postCount, 'post', 'posts'))
                  .icon(VscTag)
                  .child(
                    S.documentList()
                      .title(formatTitle(tag.title, tag.postCount, 'post', 'posts'))
                      .schemaType(postSchemaType)
                      .filter(
                        `_type == $type && !(_id in path("drafts.**")) && references($tagId)`,
                      )
                      .params({type: postSchemaType, tagId: tag._id}),
                  ),
              ),
            ])
        }),
      ),
    )
}
